'use client';

import { useId, useState } from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Snackbar from '@mui/material/Snackbar';
import SvgIcon from '@mui/material/SvgIcon';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import XIcon from '@mui/icons-material/X';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { T, useTranslation } from '@/i18n/LocaleProvider';
import { Button } from '@/components';
import { siteConfig } from '@/libs/config';
import { GTM_EVENTS } from '@/constants/gtm';
import { sendGTMEvent } from '@/utils/analytics';

// MUI no trae el ícono de Bluesky: mariposa de 24×24 en un SvgIcon, hereda el color del texto.
function BlueskyIcon(props) {
  return (
    <SvgIcon viewBox="0 0 24 24" {...props}>
      <path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.815 2.736 3.713 3.66 6.383 3.364.136-.02.275-.039.415-.056-.138.022-.276.04-.415.056-3.912.58-7.387 2.005-2.83 7.078 5.013 5.19 6.87-1.113 7.823-4.308.953 3.195 2.05 9.271 7.733 4.308 4.267-4.308 1.172-6.498-2.74-7.078a8.741 8.741 0 0 1-.415-.056c.14.017.279.036.415.056 2.67.297 5.568-.628 6.383-3.364.246-.828.624-5.79.624-6.478 0-.69-.139-1.861-.902-2.206-.659-.298-1.664-.62-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8Z" />
    </SvgIcon>
  );
}

// El enlace compartido lleva la red de origen para medir de dónde vienen las visitas.
const conRed = (url, red) => `${url}?utm_source=${red}&utm_medium=social`;

const REDES = [
  {
    id: 'x',
    label: 'Compartir en X',
    Icon: XIcon,
    href: (url, titulo) =>
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(titulo)}&url=${encodeURIComponent(conRed(url, 'x'))}`,
  },
  {
    id: 'bluesky',
    label: 'Compartir en Bluesky',
    Icon: BlueskyIcon,
    href: (url, titulo) =>
      `https://bsky.app/intent/compose?text=${encodeURIComponent(`${titulo} ${conRed(url, 'bluesky')}`)}`,
  },
  {
    id: 'linkedin',
    label: 'Compartir en LinkedIn',
    Icon: LinkedInIcon,
    href: (url) =>
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(conRed(url, 'linkedin'))}`,
  },
];

async function copiar(texto) {
  try {
    await navigator.clipboard.writeText(texto);
    return true;
  } catch {
    return false;
  }
}

/**
 * Botón «Compartir» con menú hacia arriba: Copiar enlace y las tres redes.
 * Props: `title` (texto plano), `path` (ruta canónica con barra final), `slug` y `location`
 * (dónde está el botón, para el dataLayer: 'sidebar' | 'final').
 */
export default function ShareMenu({ title, path, slug, location }) {
  const t = useTranslation();
  const menuId = useId();
  const [anchor, setAnchor] = useState(null);
  const [aviso, setAviso] = useState(false);
  const abierto = Boolean(anchor);
  const url = `${siteConfig.url}${path}`;

  const registrar = (red) =>
    sendGTMEvent({
      event: GTM_EVENTS.SHARE,
      share_network: red,
      share_location: location,
      article_slug: slug,
    });

  const cerrar = () => setAnchor(null);

  const copiarEnlace = async () => {
    cerrar();
    registrar('copiar_enlace');
    if (await copiar(conRed(url, 'enlace'))) setAviso(true);
  };

  return (
    <>
      <Button
        variant="ghost"
        size="medium"
        aria-haspopup="menu"
        aria-controls={abierto ? menuId : undefined}
        aria-expanded={abierto ? 'true' : undefined}
        onClick={(e) => setAnchor(e.currentTarget)}
        startIcon={<ShareOutlinedIcon aria-hidden="true" />}
        endIcon={
          <ExpandLessIcon
            aria-hidden="true"
            sx={{ transition: 'transform 0.15s', transform: abierto ? 'rotate(180deg)' : 'none' }}
          />
        }
      >
        Compartir
      </Button>
      <Menu
        id={menuId}
        anchorEl={anchor}
        open={abierto}
        onClose={cerrar}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: -1, minWidth: 220 } } }}
      >
        <MenuItem onClick={copiarEnlace}>
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            <T>Copiar enlace</T>
          </ListItemText>
        </MenuItem>
        {REDES.map(({ id, label, Icon, href }) => (
          <MenuItem
            key={id}
            component="a"
            href={href(url, title)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              registrar(id);
              cerrar();
            }}
          >
            <ListItemIcon>
              <Icon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              <T>{label}</T>
            </ListItemText>
          </MenuItem>
        ))}
      </Menu>
      <Snackbar
        open={aviso}
        autoHideDuration={2500}
        onClose={() => setAviso(false)}
        message={t('Enlace copiado')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
}
